import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';

const ChatContainer = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  bottom: ${props => props.$isOpen ? '0' : '-450px'};
  right: 20px;
  width: 400px;
  height: 500px;
  background: white;
  border-radius: 12px 12px 0 0;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  transition: bottom 0.3s ease;
  z-index: 1000;
  border: 1px solid #e2e8f0;
  border-bottom: none;
`;

const ChatButton = styled.button<{ $isVisible: boolean }>`
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  cursor: pointer;
  display: ${props => props.$isVisible ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  font-size: 24px;
  z-index: 999;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 16px rgba(102, 126, 234, 0.5);
  }
`;

const ChatHeader = styled.div`
  padding: 16px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px 12px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
`;

const ChatTitle = styled.div`
  font-weight: 600;
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ChatToggle = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #f7fafc;
`;

const Message = styled.div<{ isUser: boolean }>`
  display: flex;
  justify-content: ${props => props.isUser ? 'flex-end' : 'flex-start'};
  
  & > div {
    max-width: 80%;
    padding: 12px 16px;
    border-radius: 12px;
    background: ${props => props.isUser ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'white'};
    color: ${props => props.isUser ? 'white' : '#1a202c'};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    font-size: 14px;
    line-height: 1.5;
    word-wrap: break-word;
  }
`;

const InputContainer = styled.div`
  padding: 16px;
  background: white;
  border-top: 1px solid #e2e8f0;
  display: flex;
  gap: 8px;
`;

const Input = styled.input`
  flex: 1;
  padding: 10px 14px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s;
  
  &:focus {
    border-color: #667eea;
  }
`;

const SendButton = styled.button`
  padding: 10px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const LoadingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #64748b;
  font-size: 12px;
  padding: 8px 16px;
`;

interface MessageType {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

interface AIChatPanelProps {
  context?: {
    transactionId?: number;
    positionId?: number;
    cusip?: string;
    portfolioId?: number;
  };
}

const AIChatPanel: React.FC<AIChatPanelProps> = ({ context }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<MessageType[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Add welcome message
      setMessages([{
        id: 'welcome',
        text: context 
          ? "Hello! I can help you understand this transaction, position, or security. What would you like to know?"
          : "Hello! I'm your AI assistant. I can help you with questions about transactions, positions, and securities. What would you like to know?",
        isUser: false,
        timestamp: new Date()
      }]);
    }
  }, [isOpen, context]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage: MessageType = {
      id: Date.now().toString(),
      text: input,
      isUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await axios.post('/api/ai/chat/ask', {
        question: input,
        context: context || {}
      }, {
        timeout: 30000
      });

      const aiMessage: MessageType = {
        id: (Date.now() + 1).toString(),
        text: response.data.answer || 'I couldn\'t generate a response. Please try again.',
        isUser: false,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error: any) {
      console.error('Error sending message:', error);
      const errorMessage: MessageType = {
        id: (Date.now() + 1).toString(),
        text: 'Sorry, I encountered an error: ' + (error.response?.data?.answer || error.message || 'Unknown error'),
        isUser: false,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Floating Chat Button - Always visible when panel is closed */}
      <ChatButton 
        $isVisible={!isOpen}
        onClick={() => setIsOpen(true)}
        title="Open AI Assistant"
      >
        🤖
      </ChatButton>

      <ChatContainer $isOpen={isOpen}>
        <ChatHeader onClick={() => setIsOpen(!isOpen)}>
          <ChatTitle>
            🤖 AI Assistant
            {context && (
              <span style={{ fontSize: '12px', opacity: 0.9 }}>
                {context.transactionId ? ` (Transaction #${context.transactionId})` : ''}
                {context.positionId ? ` (Position #${context.positionId})` : ''}
                {context.cusip ? ` (${context.cusip})` : ''}
                {context.portfolioId ? ` (Portfolio #${context.portfolioId})` : ''}
              </span>
            )}
          </ChatTitle>
          <ChatToggle onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen); }}>
            {isOpen ? '▼' : '▲'}
          </ChatToggle>
        </ChatHeader>
      
        {isOpen && (
          <>
            <MessagesContainer>
              {messages.map((message) => (
                <Message key={message.id} isUser={message.isUser}>
                  <div>{message.text}</div>
                </Message>
              ))}
              {loading && (
                <LoadingIndicator>
                  <span>🔄</span>
                  <span>Thinking...</span>
                </LoadingIndicator>
              )}
              <div ref={messagesEndRef} />
            </MessagesContainer>
            
            <InputContainer>
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask a question..."
                disabled={loading}
              />
              <SendButton onClick={handleSend} disabled={loading || !input.trim()}>
                Send
              </SendButton>
            </InputContainer>
          </>
        )}
      </ChatContainer>
    </>
  );
};

export default AIChatPanel;

